import { useState, useEffect, useRef } from 'react';
import * as Ably from 'ably';
import Confetti from './Confetti';

const API      = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = `${API}/ably/token`;

// ─── Ably singleton ───────────────────────────────────────────────────────────
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({
    authUrl: ABLY_AUTH_URL,
    autoConnect: true,
    // On HTTP LAN, WSS connectivity check always fails — shorten timeout so
    // Ably falls back to XHR polling quickly instead of waiting the full default.
    webSocketConnectTimeout: 4000,
    disconnectedRetryTimeout: 3000,
  });
  return _ablyClient;
}
function useAblyChannel(channelName, handlers) {
  const ref = useRef(handlers);
  useEffect(() => { ref.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client = getAblyClient();
    if (!client) return;
    const ch   = client.channels.get(channelName);
    const subs = Object.keys(ref.current).map(ev => {
      const cb = (m) => ref.current[ev]?.(m.data);
      ch.subscribe(ev, cb);
      return { ev, cb };
    });
    return () => subs.forEach(({ ev, cb }) => ch.unsubscribe(ev, cb));
  }, [channelName]);
}

// ─── Colour palette ───────────────────────────────────────────────────────────
const C = {
  bg:      '#060914',
  gold:    '#FFB300',
  goldDim: '#996900',
  teal:    '#00E5CC',
  tealDim: '#007A6B',
  green:   '#00C853',
  red:     '#FF3D5A',
  purple:  '#7C4DFF',
  surface: '#0D1526',
};

// ─── Global styles + keyframes ────────────────────────────────────────────────
const STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { overflow: hidden; touch-action: none; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(0,229,204,.25); border-radius: 2px; }

  @keyframes crFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes crPop {
    0%   { transform: scale(0.55); opacity: 0; }
    65%  { transform: scale(1.14); }
    100% { transform: scale(1);    opacity: 1; }
  }
  @keyframes crGlow {
    0%,100% { box-shadow: 0 0 18px rgba(0,229,204,.35), 0 0 0 2px rgba(0,229,204,.4); }
    50%      { box-shadow: 0 0 44px rgba(0,229,204,.75), 0 0 0 2px rgba(0,229,204,.8); }
  }
  @keyframes crWrongShake {
    0%,100% { transform: translateX(-50%); }
    18%     { transform: translateX(calc(-50% - 10px)); }
    36%     { transform: translateX(calc(-50% + 10px)); }
    54%     { transform: translateX(calc(-50% - 7px)); }
    72%     { transform: translateX(calc(-50% + 7px)); }
    90%     { transform: translateX(calc(-50% - 3px)); }
  }
  @keyframes crLetterDrop {
    0%   { opacity: 0; transform: translateY(-18px) scale(1.35); filter: blur(3px); }
    60%  { opacity: 1; transform: translateY(2px) scale(0.95); filter: blur(0); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes crSlotChar {
    0%   { opacity: 0.5; transform: scaleY(1.4) translateY(-2px); }
    50%  { opacity: 1;   transform: scaleY(0.7) translateY(2px); }
    100% { opacity: 0.5; transform: scaleY(1.4) translateY(-2px); }
  }
  @keyframes crChainComplete {
    0%,100% { opacity: 1; }
    25%     { opacity: 0.6; filter: hue-rotate(60deg) brightness(1.4); }
    50%     { opacity: 1;   filter: hue-rotate(120deg) brightness(1.6); }
    75%     { opacity: 0.7; filter: hue-rotate(200deg) brightness(1.3); }
  }
  @keyframes crFloat {
    0%,100% { transform: translateY(0) rotate(-2deg); }
    50%     { transform: translateY(-18px) rotate(2deg); }
  }
  @keyframes crOrb1 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%     { transform: translate(70px,-45px) scale(1.18); }
  }
  @keyframes crOrb2 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%     { transform: translate(-55px,38px) scale(0.88); }
  }
  @keyframes crOrb3 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%     { transform: translate(30px,55px) scale(1.1); }
  }
  @keyframes crConfetti {
    0%   { transform: translateY(-5vh) rotate(0deg); opacity: 1; }
    100% { transform: translateY(108vh) rotate(760deg); opacity: 0; }
  }
  @keyframes crSlideDown {
    from { opacity: 0; transform: translateX(-50%) translateY(-32px) scale(0.94); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  }
  @keyframes crPodiumRise {
    from { opacity: 0; transform: translateX(60px) scale(0.85); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes crConnectorPulse {
    0%,100% { opacity: 0.3; }
    50%     { opacity: 0.7; }
  }
  @keyframes crSolvedGlow {
    0%,100% { box-shadow: 0 0 0 0 rgba(0,200,83,.0); }
    50%     { box-shadow: 0 0 30px rgba(0,200,83,.55); }
  }
  @keyframes crHostSlide {
    from { opacity: 0; transform: translateY(-8px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }
  @keyframes crToastUp {
    from { opacity: 0; transform: translateX(-50%) translateY(14px) scale(0.95); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1); }
  }
  @keyframes crRoundIn {
    from { opacity: 0; transform: scale(0.88) translateY(30px); filter: blur(4px); }
    to   { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0); }
  }
  @keyframes crCountdownShrink {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }
  @keyframes crPulseGold {
    0%,100% { opacity: 1; }
    50%     { opacity: 0.55; }
  }
  @keyframes crScan {
    from { left: -30%; }
    to   { left: 110%; }
  }
  @keyframes crInstructionSlide {
    from { opacity: 0; transform: translateY(12px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }
  @keyframes crOverlayIn {
    from { opacity: 0; transform: scale(0.94); filter: blur(6px); }
    to   { opacity: 1; transform: scale(1);    filter: blur(0); }
  }
  @keyframes crHintPulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(255,179,0,0); }
    50%     { box-shadow: 0 0 60px rgba(255,179,0,.45), 0 0 0 4px rgba(255,179,0,.3); }
  }
  @keyframes crSpeedTimer {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }
  @keyframes crBonusPulse {
    0%,100% { text-shadow: 0 0 20px rgba(124,77,255,.5); }
    50%     { text-shadow: 0 0 60px rgba(124,77,255,1), 0 0 100px rgba(124,77,255,.4); }
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function buildLetterPattern(word, revealedCountOrPositions) {
  if (!word) return '';
  const upper = word.toUpperCase();
  if (Array.isArray(revealedCountOrPositions)) {
    const posSet = new Set(revealedCountOrPositions);
    return upper.split('').map((ch, i) => posSet.has(i) ? ch : '_').join(' ');
  }
  return upper.split('').map((ch, i) => i < (revealedCountOrPositions || 0) ? ch : '_').join(' ');
}
function teamColor(teamId, teamScores) {
  if (!teamId || !teamScores?.length) return C.teal;
  const t = teamScores.find(t => t.id === teamId);
  return t?.color || C.teal;
}
function normScores(s) {
  if (!s) return [];
  return (Array.isArray(s) ? s : Object.values(s)).sort((a, b) => b.score - a.score);
}

// Confetti is now the shared canvas component (imported above).

// ─── Background orbs ──────────────────────────────────────────────────────────
function Orbs() {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
      <div style={{ position: 'absolute', width: '72vw', height: '72vw', top: '-28%', left: '-22%', background: `radial-gradient(circle, rgba(0,229,204,.07) 0%, transparent 62%)`, animation: 'crOrb1 14s ease infinite' }} />
      <div style={{ position: 'absolute', width: '60vw', height: '60vw', bottom: '-22%', right: '-18%', background: `radial-gradient(circle, rgba(255,179,0,.065) 0%, transparent 62%)`, animation: 'crOrb2 18s ease infinite 4s' }} />
      <div style={{ position: 'absolute', width: '44vw', height: '44vw', top: '35%', right: '28%', background: `radial-gradient(circle, rgba(124,77,255,.055) 0%, transparent 62%)`, animation: 'crOrb3 22s ease infinite 8s' }} />
    </div>
  );
}

// ─── Slot machine character ───────────────────────────────────────────────────
// Cycles through random letters then settles on '_'. Re-mounts via key when slot changes.
function SlotMachineChar({ position, totalChars }) {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const [display, setDisplay] = useState(() => CHARS[Math.floor(Math.random() * 26)]);
  const [settled, setSettled] = useState(false);
  const ivRef = useRef(null);

  useEffect(() => {
    const startTime = Date.now();
    const settleDuration = 700 + position * Math.max(100, Math.round(500 / Math.max(totalChars, 1)));
    ivRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= settleDuration) {
        clearInterval(ivRef.current);
        setDisplay('_');
        setSettled(true);
        return;
      }
      const progress = elapsed / settleDuration;
      if (progress > 0.6 && Math.random() > 0.45) return;
      setDisplay(CHARS[Math.floor(Math.random() * 26)]);
    }, 55);
    return () => clearInterval(ivRef.current);
  }, []); // eslint-disable-line

  return (
    <span style={{
      display: 'inline-block',
      minWidth: 'clamp(12px,1.4vw,22px)',
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: 'clamp(22px,2.8vw,38px)',
      fontWeight: 700,
      color: settled ? 'rgba(255,255,255,.22)' : C.teal,
      textShadow: settled ? 'none' : `0 0 10px ${C.teal}`,
      borderBottom: settled ? '2px solid rgba(255,255,255,.15)' : `2px solid ${C.teal}55`,
      paddingBottom: '2px', letterSpacing: 1,
      animation: !settled ? `crSlotChar ${0.10 + position * 0.015}s ease infinite` : 'none',
      transition: 'color .2s ease, text-shadow .2s ease, border-color .2s ease',
    }}>{display}</span>
  );
}

// ─── Letter pattern ───────────────────────────────────────────────────────────
function LetterPattern({ pattern, fontSize, solved, isActive, slotKey }) {
  if (!pattern) return null;
  const chars = pattern.split('');
  const totalChars = chars.filter(c => c !== ' ').length;
  let charIdx = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'clamp(3px,.4vw,7px)', flexWrap: 'wrap', justifyContent: 'center' }}>
      {chars.map((ch, i) => {
        if (ch === ' ') return <span key={i} style={{ width: 'clamp(6px,.8vw,12px)', display: 'inline-block' }} />;
        const pos = charIdx++;
        const isBlank = ch === '_';
        if (isActive && isBlank && !solved) {
          return <SlotMachineChar key={`${slotKey}-${pos}`} position={pos} totalChars={totalChars} />;
        }
        return (
          <span key={i} style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: fontSize || 'clamp(22px,2.8vw,38px)',
            fontWeight: 700,
            color: solved ? C.green : isBlank ? 'rgba(255,255,255,.22)' : '#FFFFFF',
            textShadow: !isBlank && !solved ? '0 0 14px rgba(255,255,255,.6)' : solved ? `0 0 18px ${C.green}88` : 'none',
            animation: !isBlank && !solved ? `crLetterDrop .42s ${pos * 0.055}s cubic-bezier(.34,1.2,.64,1) both` : 'none',
            display: 'inline-block',
            minWidth: isBlank ? 'clamp(12px,1.4vw,22px)' : undefined,
            borderBottom: isBlank ? '2px solid rgba(255,255,255,.15)' : 'none',
            paddingBottom: isBlank ? '2px' : '0',
            letterSpacing: isBlank ? 0 : 1,
          }}>{ch}</span>
        );
      })}
    </div>
  );
}

// ─── Single chain slot card ───────────────────────────────────────────────────
function SlotCard({ slotData, idx, isActive, isSolved, isTop, isBottom, mode, totalSlots }) {
  const word          = slotData?.word || '';
  const letterPattern = slotData?.letterPattern || '';
  const solvedBy      = slotData?.solvedByName || slotData?.solvedBy || '';

  if (isTop) return (
    <div style={{ background: `linear-gradient(135deg, rgba(255,179,0,.18), rgba(255,179,0,.08))`, border: `2px solid ${C.gold}88`, borderRadius: 'clamp(10px,1.2vw,16px)', padding: 'clamp(10px,1.5vh,18px) clamp(16px,2vw,28px)', textAlign: 'center', boxShadow: `0 0 28px rgba(255,179,0,.22), 0 4px 24px rgba(0,0,0,.5)`, animation: 'crFadeUp .6s ease both' }}>
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(10px,1vw,13px)', color: C.goldDim, marginBottom: 'clamp(3px,.4vh,6px)' }}>START</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(26px,3.2vw,50px)', color: C.gold, letterSpacing: 3, textShadow: `0 0 30px ${C.gold}66` }}>{word.toUpperCase()}</div>
    </div>
  );

  if (isBottom) return (
    <div style={{ background: `linear-gradient(135deg, rgba(0,229,204,.18), rgba(0,229,204,.08))`, border: `2px solid ${C.teal}88`, borderRadius: 'clamp(10px,1.2vw,16px)', padding: 'clamp(10px,1.5vh,18px) clamp(16px,2vw,28px)', textAlign: 'center', boxShadow: `0 0 28px rgba(0,229,204,.22), 0 4px 24px rgba(0,0,0,.5)`, animation: `crFadeUp .6s ${totalSlots * 0.06}s ease both` }}>
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(10px,1vw,13px)', color: C.tealDim, marginBottom: 'clamp(3px,.4vh,6px)' }}>END</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(26px,3.2vw,50px)', color: C.teal, letterSpacing: 3, textShadow: `0 0 30px ${C.teal}66` }}>{word.toUpperCase()}</div>
    </div>
  );

  const animDelay = `${idx * 0.07}s`;
  const cardBg = isSolved
    ? `linear-gradient(135deg, rgba(0,200,83,.16), rgba(0,200,83,.06))`
    : isActive ? `linear-gradient(135deg, rgba(0,229,204,.12), rgba(0,229,204,.05))` : `rgba(13,21,38,.85)`;
  const cardBorder = isSolved ? `2px solid ${C.green}88` : isActive ? `2px solid ${C.teal}88` : `1px solid rgba(255,255,255,.09)`;

  return (
    <div style={{
      background: cardBg, border: cardBorder, borderRadius: 'clamp(10px,1.2vw,16px)',
      padding: 'clamp(8px,1.2vh,16px) clamp(12px,1.6vw,24px)', textAlign: 'center',
      animation: isActive && !isSolved ? 'crGlow 2.2s ease infinite' : isSolved ? `crPop .5s ${animDelay} cubic-bezier(.34,1.4,.64,1) both` : `crFadeUp .55s ${animDelay} ease both`,
      position: 'relative', overflow: 'hidden',
      transition: 'background .4s ease, border-color .4s ease',
    }}>
      {/* Scan shimmer on active card */}
      {isActive && !isSolved && (
        <div style={{ position: 'absolute', top: 0, bottom: 0, width: '35%', background: 'linear-gradient(90deg, transparent, rgba(0,229,204,.08), transparent)', animation: 'crScan 2.4s ease infinite', pointerEvents: 'none' }} />
      )}
      {/* Active indicator dot */}
      {isActive && !isSolved && (
        <div style={{ position: 'absolute', top: 'clamp(7px,1vh,12px)', right: 'clamp(10px,1.2vw,16px)', width: 'clamp(6px,.7vw,10px)', height: 'clamp(6px,.7vw,10px)', borderRadius: '50%', background: C.teal, boxShadow: `0 0 8px ${C.teal}`, animation: 'crConnectorPulse 1.2s ease infinite' }} />
      )}

      {isSolved ? (
        <>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(22px,2.8vw,42px)', color: C.green, letterSpacing: 3, textShadow: `0 0 24px ${C.green}66` }}>{word.toUpperCase()}</div>
          {solvedBy && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(10px,1vw,14px)', color: `${C.green}99`, letterSpacing: 2, marginTop: 'clamp(2px,.3vh,5px)' }}>{solvedBy}</div>}
        </>
      ) : isActive ? (
        <LetterPattern
          pattern={mode === 'coop'
            ? (word ? word.split('').map(() => '_').join(' ') : '_ _ _ _ _')
            : (letterPattern || (word ? word.split('').map(() => '_').join(' ') : '_ _ _ _'))}
          fontSize="clamp(22px,2.8vw,38px)"
          solved={false} isActive={true}
          slotKey={`${word}-${idx}`}
        />
      ) : (
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(14px,1.6vw,22px)', color: 'rgba(255,255,255,.18)', letterSpacing: 4 }}>{'_ _ _ _ _'}</div>
      )}
    </div>
  );
}

// ─── Chain connector ──────────────────────────────────────────────────────────
function Connector({ solved }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(1px,.2vh,3px) 0', flexShrink: 0 }}>
      <div style={{ width: 2, height: 'clamp(10px,1.4vh,20px)', background: solved ? `linear-gradient(180deg, ${C.green}88, ${C.green}22)` : 'rgba(255,255,255,.12)', borderRadius: 1 }} />
      <div style={{ position: 'absolute', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${solved ? C.green + '88' : 'rgba(255,255,255,.15)'}`, marginTop: 'clamp(14px,1.8vh,26px)', animation: solved ? 'none' : 'crConnectorPulse 2s ease infinite' }} />
    </div>
  );
}

// ─── Host message bar ─────────────────────────────────────────────────────────
function HostMessageBar({ msg, msgId }) {
  if (!msg) return null;
  return (
    <div key={msgId} style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 450,
      background: 'linear-gradient(90deg, rgba(6,9,20,.97) 0%, rgba(255,179,0,.1) 40%, rgba(255,179,0,.1) 60%, rgba(6,9,20,.97) 100%)',
      borderBottom: `1px solid rgba(255,179,0,.25)`,
      padding: 'clamp(7px,1vh,12px) clamp(12px,2vw,36px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(6px,.8vw,10px)',
      backdropFilter: 'blur(14px)',
      pointerEvents: 'none',
      animation: 'crHostSlide .3s ease both',
      overflow: 'hidden',
    }}>
      <span style={{ fontSize: 'clamp(14px,1.6vw,22px)', flexShrink: 0 }}>🎙️</span>
      <span style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontWeight: 700,
        letterSpacing: 2,
        fontSize: 'clamp(12px,1.4vw,19px)',
        color: C.gold,
        textShadow: `0 0 20px rgba(255,179,0,.45)`,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '90vw',
      }}>{msg.msg || msg}</span>
    </div>
  );
}

// ─── Round badge ──────────────────────────────────────────────────────────────
function RoundBadge({ current, total }) {
  if (!total || total <= 1) return null;
  const isFinal = current + 1 >= total;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: isFinal ? 'rgba(255,61,90,.12)' : 'rgba(255,179,0,.12)', border: `1px solid ${isFinal ? C.red : C.gold}44`, borderRadius: 20, padding: '4px 14px', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 3, fontSize: 'clamp(11px,1.1vw,15px)', color: isFinal ? C.red : C.gold, animation: isFinal ? 'crPulseGold 1.5s ease infinite' : 'crFadeUp .5s ease both' }}>
      {isFinal ? '🏁 FINAL' : `ROUND ${current + 1}`}
      {!isFinal && <span style={{ opacity: .4 }}>/ {total}</span>}
    </div>
  );
}

// ─── Round transition overlay ─────────────────────────────────────────────────
function RoundTransitionScreen({ data, scores, teamScores }) {
  if (!data) return null;
  const medals = ['🥇', '🥈', '🥉'];
  const sorted = [...(scores || [])].sort((a, b) => b.score - a.score).slice(0, 5);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 350, background: 'rgba(6,9,20,.93)', backdropFilter: 'blur(18px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'clamp(10px,1.8vh,26px)', animation: 'crRoundIn .5s cubic-bezier(.34,1.2,.64,1) both', overflowY: 'auto', padding: 'clamp(12px,2vh,28px)' }}>
      <div style={{ fontSize: 'clamp(36px,min(6vw,8vh),80px)', lineHeight: 1 }}>🔗</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(28px,min(5vw,7.5vh),76px)', color: C.gold, letterSpacing: 8, lineHeight: 1, textShadow: `0 0 60px rgba(255,179,0,.6)`, animation: 'crChainComplete 3s ease infinite' }}>
        ROUND {data.fromRound + 1} COMPLETE!
      </div>
      {sorted.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 16, padding: 'clamp(14px,2vh,24px) clamp(20px,3vw,40px)', minWidth: 'min(380px,80vw)', animation: 'crFadeUp .6s .2s ease both' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(10px,1vw,13px)', color: 'rgba(255,179,0,.45)', marginBottom: 'clamp(8px,1.2vh,14px)', textAlign: 'center' }}>STANDINGS</div>
          {sorted.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'clamp(5px,.7vh,9px) 0', borderBottom: i < sorted.length - 1 ? '1px solid rgba(255,255,255,.06)' : 'none' }}>
              <span style={{ fontSize: 'clamp(18px,2vw,28px)', width: 32, flexShrink: 0 }}>{medals[i] || `${i + 1}.`}</span>
              {s.teamId && teamScores?.length > 0 && <div style={{ width: 5, height: 28, borderRadius: 3, background: teamColor(s.teamId, teamScores), flexShrink: 0 }} />}
              <span style={{ flex: 1, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(15px,1.7vw,23px)', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(16px,1.9vw,26px)', color: i === 0 ? C.gold : 'rgba(255,255,255,.55)', flexShrink: 0 }}>{s.score}</span>
            </div>
          ))}
        </div>
      )}
      {data.hasMore && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 4, fontSize: 'clamp(14px,1.8vw,26px)', color: C.teal, animation: 'crFadeUp .6s .35s ease both' }}>⚡ ROUND {data.toRound + 1} INCOMING…</div>}
    </div>
  );
}

// ─── Countdown bar ────────────────────────────────────────────────────────────
function CountdownBar({ countdown }) {
  if (!countdown) return null;
  const sec = Math.round(countdown.delayMs / 1000);
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 340, background: 'rgba(0,229,204,.08)', borderTop: `1px solid rgba(0,229,204,.2)`, padding: 'clamp(8px,1vh,14px) clamp(16px,2vw,32px)', display: 'flex', alignItems: 'center', gap: 'clamp(10px,1.4vw,20px)' }}>
      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 3, fontSize: 'clamp(12px,1.3vw,18px)', color: C.teal, flexShrink: 0 }}>NEXT ROUND IN {sec}s</span>
      <div style={{ flex: 1, height: 4, background: 'rgba(0,229,204,.15)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: `linear-gradient(90deg, ${C.teal}, ${C.gold})`, borderRadius: 2, transformOrigin: 'left', animation: `crCountdownShrink ${sec}s linear forwards` }} />
      </div>
    </div>
  );
}

// ─── Leaderboard panel ────────────────────────────────────────────────────────
function LeaderboardPanel({ scores, teamScores, game, status, currentSlot, totalMiddleSlots, currentRound, totalRounds }) {
  const hasTeams = teamScores?.length > 0;
  const mode     = game?.mode || 'buzzin';
  const medals   = ['🥇', '🥈', '🥉'];
  const topWord    = game?.chain?.top    || '';
  const bottomWord = game?.chain?.bottom || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px,1.4vh,18px)', height: '100%', overflowY: 'auto' }}>
      <div style={{ animation: 'crFadeUp .5s ease both' }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(22px,2.8vw,42px)', color: C.gold, letterSpacing: 5, textShadow: `0 0 28px ${C.gold}55`, lineHeight: 1 }}>CHAIN REACTION</div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'clamp(5px,.7vw,8px)', marginTop: 'clamp(5px,.7vh,9px)' }}>
          <span style={{ display: 'inline-block', background: mode === 'coop' ? 'rgba(124,77,255,.18)' : 'rgba(0,229,204,.14)', border: `1px solid ${mode === 'coop' ? C.purple : C.teal}55`, borderRadius: 8, color: mode === 'coop' ? C.purple : C.teal, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 3, fontSize: 'clamp(10px,1vw,14px)', padding: '3px 10px' }}>{mode === 'coop' ? 'CO-OP' : 'BUZZ-IN'}</span>
          {totalRounds > 1 && <RoundBadge current={currentRound} total={totalRounds} />}
          {game?.id && <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(10px,1vw,14px)', color: 'rgba(255,255,255,.3)', letterSpacing: 2 }}>#{game.id}</span>}
        </div>
      </div>

      {(topWord || bottomWord) && (
        <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 'clamp(8px,1vw,14px)', padding: 'clamp(10px,1.3vh,18px) clamp(12px,1.6vw,22px)', animation: 'crFadeUp .6s .1s ease both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px,1vw,14px)', justifyContent: 'center' }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(14px,1.5vw,22px)', color: C.gold, letterSpacing: 2 }}>{topWord.toUpperCase()}</span>
            <span style={{ color: 'rgba(255,255,255,.25)', fontSize: 'clamp(12px,1.2vw,18px)' }}>→</span>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(14px,1.5vw,22px)', color: C.teal, letterSpacing: 2 }}>{bottomWord.toUpperCase()}</span>
          </div>
          {totalMiddleSlots > 0 && <div style={{ textAlign: 'center', marginTop: 'clamp(4px,.5vh,7px)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(10px,1vw,13px)', color: 'rgba(255,255,255,.3)', letterSpacing: 2 }}>{currentSlot + 1} / {totalMiddleSlots} LINKS</div>}
        </div>
      )}

      {status === 'active' && (
        <div style={{ background: 'rgba(0,229,204,.08)', border: `1px solid ${C.teal}33`, borderRadius: 'clamp(8px,1vw,14px)', padding: 'clamp(8px,1vh,14px) clamp(10px,1.4vw,18px)', textAlign: 'center', animation: 'crFadeUp .7s .15s ease both' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(12px,1.2vw,17px)', color: C.teal, letterSpacing: 2 }}>TEXT YOUR ANSWER</div>
          <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 'clamp(10px,1vw,14px)', color: 'rgba(255,255,255,.4)', marginTop: 3, letterSpacing: 1 }}>via WhatsApp to play</div>
        </div>
      )}

      {hasTeams && (
        <div style={{ animation: 'crFadeUp .65s .2s ease both' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(10px,1vw,14px)', color: 'rgba(255,179,0,.45)', marginBottom: 'clamp(5px,.7vh,9px)' }}>TEAMS</div>
          {teamScores.map((t, i) => (
            <div key={t.id || i} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(7px,.9vw,12px)', padding: 'clamp(5px,.7vh,8px) 0', borderBottom: i < teamScores.length - 1 ? '1px solid rgba(255,255,255,.05)' : 'none' }}>
              <div style={{ width: 'clamp(7px,.8vw,10px)', height: 'clamp(7px,.8vw,10px)', borderRadius: '50%', background: t.color || C.teal, flexShrink: 0, boxShadow: `0 0 7px ${t.color || C.teal}` }} />
              <span style={{ flex: 1, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(13px,1.4vw,19px)', color: i === 0 ? C.gold : 'rgba(255,255,255,.75)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(14px,1.6vw,22px)', color: i === 0 ? C.gold : (t.color || 'rgba(255,255,255,.5)'), flexShrink: 0 }}>{t.score}</span>
            </div>
          ))}
        </div>
      )}

      {scores?.length > 0 && (
        <div style={{ flex: 1, animation: 'crFadeUp .7s .25s ease both' }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(10px,1vw,14px)', color: 'rgba(255,179,0,.45)', marginBottom: 'clamp(6px,.8vh,10px)' }}>LEADERBOARD</div>
          {scores.slice(0, 8).map((s, i) => (
            <div key={(s.jid || s.name) + i} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(7px,.9vw,12px)', padding: 'clamp(5px,.7vh,9px) 0', borderBottom: i < Math.min(scores.length, 8) - 1 ? '1px solid rgba(255,255,255,.05)' : 'none' }}>
              <span style={{ fontSize: 'clamp(14px,1.5vw,20px)', width: 'clamp(20px,2vw,28px)', flexShrink: 0 }}>{medals[i] || `${i + 1}.`}</span>
              {s.teamId && teamScores?.length > 0 && <div style={{ width: 'clamp(5px,.5vw,7px)', height: 'clamp(20px,2.5vh,32px)', borderRadius: 3, background: teamColor(s.teamId, teamScores), flexShrink: 0 }} />}
              <span style={{ flex: 1, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(13px,1.4vw,19px)', color: 'rgba(255,255,255,.88)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(14px,1.6vw,22px)', color: i === 0 ? C.gold : 'rgba(255,255,255,.5)', flexShrink: 0 }}>{s.score}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Flash banner ─────────────────────────────────────────────────────────────
function FlashBanner({ lastSolved, wrongFlash }) {
  if (!lastSolved && !wrongFlash) return null;
  if (lastSolved) return (
    <div style={{ position: 'fixed', bottom: 'clamp(24px,3.5vh,48px)', left: '50%', transform: 'translateX(-50%)', maxWidth: 'min(92vw, 800px)', background: `linear-gradient(135deg, rgba(0,200,83,.22), rgba(0,200,83,.1))`, border: `2px solid ${C.green}88`, borderRadius: 'clamp(10px,1.2vw,18px)', padding: 'clamp(10px,1.5vh,18px) clamp(18px,2.5vw,36px)', zIndex: 500, animation: 'crSlideDown .35s cubic-bezier(.34,1.2,.64,1) both', backdropFilter: 'blur(12px)', textAlign: 'center', boxShadow: `0 8px 40px rgba(0,0,0,.5), 0 0 50px ${C.green}33`, display: 'flex', alignItems: 'center', gap: 'clamp(8px,1vw,14px)', overflow: 'hidden' }}>
      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(20px,2.5vw,38px)', color: C.green, letterSpacing: 3, textShadow: `0 0 22px ${C.green}88`, flexShrink: 0 }}>{lastSolved.word.toUpperCase()}!</span>
      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(14px,1.6vw,24px)', color: 'rgba(255,255,255,.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{lastSolved.playerName}</span>
      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(16px,2vw,28px)', color: C.gold, flexShrink: 0 }}>+{lastSolved.points}pts</span>
    </div>
  );
  return (
    <div style={{ position: 'fixed', bottom: 'clamp(24px,3.5vh,48px)', left: '50%', transform: 'translateX(-50%)', maxWidth: 'min(88vw, 700px)', background: 'rgba(255,61,90,.14)', border: `2px solid ${C.red}77`, borderRadius: 'clamp(10px,1.2vw,18px)', padding: 'clamp(8px,1.2vh,16px) clamp(16px,2.5vw,36px)', zIndex: 500, animation: 'crWrongShake .45s ease both', backdropFilter: 'blur(12px)', textAlign: 'center', overflow: 'hidden' }}>
      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(18px,2.2vw,32px)', color: C.red, letterSpacing: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{wrongFlash.playerName}: {wrongFlash.guess}</span>
    </div>
  );
}

// ─── Waiting screen ───────────────────────────────────────────────────────────
function WaitingScreen({ game, currentRound, totalRounds }) {
  const middleCount = game ? Math.max(0, Object.keys(game.slots || {}).length - 2) : 0;
  const topWord    = game?.chain?.top    || game?.slots?.[0]?.word    || '???';
  const bottomWord = game?.chain?.bottom || game?.slots?.[Object.keys(game?.slots || {}).length - 1]?.word || '???';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'clamp(20px,4vh,60px) clamp(24px,5vw,80px)', gap: 'clamp(12px,2vh,26px)', position: 'relative', zIndex: 1 }}>
      <div style={{ fontSize: 'clamp(54px,min(9vh,11vw),120px)', animation: 'crFloat 3.2s ease infinite', lineHeight: 1 }}>🔗</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(44px,min(7vh,10vw),110px)', color: C.gold, letterSpacing: 10, lineHeight: 1, textShadow: `0 0 70px ${C.gold}77`, animation: 'crFadeUp .6s ease both' }}>CHAIN REACTION</div>
      {totalRounds > 1 && <div style={{ animation: 'crFadeUp .6s .08s ease both' }}><RoundBadge current={currentRound} total={totalRounds} /></div>}
      {game?.id && <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(26px,min(4.5vh,5.5vw),66px)', letterSpacing: 8, color: 'rgba(255,255,255,.4)', animation: 'crFadeUp .6s .12s ease both' }}>{game.id}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px,1.5vw,22px)', animation: 'crFadeUp .65s .18s ease both', flexWrap: 'wrap', justifyContent: 'center' }}>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(22px,2.8vw,42px)', color: C.gold, letterSpacing: 3, background: 'rgba(255,179,0,.12)', border: `1px solid ${C.gold}44`, borderRadius: 10, padding: 'clamp(6px,.8vh,10px) clamp(16px,2vw,26px)' }}>{topWord.toUpperCase()}</span>
        <span style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(18px,2.2vw,32px)' }}>{'▼'.repeat(Math.max(1, Math.min(middleCount, 5)))}</span>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(22px,2.8vw,42px)', color: C.teal, letterSpacing: 3, background: 'rgba(0,229,204,.12)', border: `1px solid ${C.teal}44`, borderRadius: 10, padding: 'clamp(6px,.8vh,10px) clamp(16px,2vw,26px)' }}>{bottomWord.toUpperCase()}</span>
      </div>
      {middleCount > 0 && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 'clamp(14px,1.6vw,22px)', color: 'rgba(255,255,255,.4)', letterSpacing: 3, animation: 'crFadeUp .7s .25s ease both' }}>{middleCount} LINK{middleCount !== 1 ? 'S' : ''} TO FIND</div>}
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(13px,1.5vw,21px)', color: 'rgba(255,255,255,.28)', letterSpacing: 3, textAlign: 'center', animation: 'crFadeUp .7s .3s ease both' }}>Text /join {game?.id || ''} on WhatsApp to play</div>
    </div>
  );
}

// ─── Completed screen ─────────────────────────────────────────────────────────
function CompletedScreen({ game, slots, scores, teamScores, onDismiss }) {
  const [countdown, setCountdown] = useState(60);
  const medals    = ['🥇', '🥈', '🥉'];
  const hasTeams  = teamScores?.length > 0;
  const topWord   = game?.chain?.top    || '';
  const botWord   = game?.chain?.bottom || '';
  const hiddenKeys = Object.keys(slots || {}).map(Number).sort((a, b) => a - b);

  useEffect(() => {
    const iv = setInterval(() => setCountdown(c => {
      if (c <= 1) { clearInterval(iv); onDismiss?.(); return 0; }
      return c - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [onDismiss]);

  const chainItems = [
    { key: 'start', word: topWord,  color: C.gold,  anchor: true },
    ...hiddenKeys.map(k => ({ key: k, word: slots[k]?.word || '?', color: C.green, anchor: false })),
    { key: 'end',   word: botWord,  color: C.teal,  anchor: true },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 'clamp(12px,2vh,32px) clamp(20px,3.5vw,56px)', gap: 'clamp(8px,1.5vh,22px)', position: 'relative', zIndex: 1, overflow: 'hidden' }}>
      <Confetti />

      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(4px,.6vh,10px)', flexShrink: 0 }}>
        <div style={{ fontSize: 'clamp(40px,7vh,90px)', lineHeight: 1 }}>🏆</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(44px,8vh,110px)', color: C.gold, letterSpacing: 8, lineHeight: 1, textShadow: `0 0 80px ${C.gold}99`, animation: 'crChainComplete 3s ease infinite' }}>CHAIN COMPLETE!</div>
      </div>

      {/* Two-column content fills remaining space */}
      <div style={{ flex: 1, display: 'flex', gap: 'clamp(20px,4vw,64px)', overflow: 'hidden' }}>

        {/* Chain column — items spread to fill full height */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', alignItems: 'center', flexBasis: '36%', flexShrink: 0, animation: 'crFadeUp .8s .2s ease both' }}>
          {chainItems.map((item, i) => (
            <div key={item.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: `crPodiumRise .5s ${i * 0.07}s cubic-bezier(.34,1.4,.64,1) both` }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: item.anchor ? 'clamp(22px,4vh,54px)' : 'clamp(18px,3.5vh,46px)', color: item.color, letterSpacing: 5, textShadow: `0 0 28px ${item.color}77`, background: `${item.color}14`, border: `2px solid ${item.color}55`, borderRadius: 10, padding: 'clamp(6px,1vh,14px) clamp(14px,2.2vw,32px)', minWidth: 'clamp(120px,16vw,240px)', textAlign: 'center' }}>{item.word.toUpperCase()}</div>
              {i < chainItems.length - 1 && <div style={{ color: 'rgba(255,255,255,.25)', fontSize: 'clamp(14px,2.2vh,28px)', lineHeight: 1 }}>▼</div>}
            </div>
          ))}
        </div>

        {/* Scores column */}
        {scores?.length > 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 'clamp(6px,1vh,14px)' }}>
            {hasTeams && teamScores?.length > 0 && (
              <>
                <div style={{ textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(11px,1.5vh,18px)', color: 'rgba(255,179,0,.45)' }}>TEAM STANDINGS</div>
                <div style={{ display: 'flex', gap: 'clamp(8px,1.5vw,20px)', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {teamScores.map((t, i) => (
                    <div key={t.id || i} style={{ background: `${t.color || C.teal}18`, border: `2px solid ${t.color || C.teal}${i === 0 ? '99' : '55'}`, borderRadius: 14, padding: 'clamp(10px,1.8vh,22px) clamp(16px,2.5vw,32px)', textAlign: 'center', animation: `crPodiumRise .5s ${i * .12}s cubic-bezier(.34,1.4,.64,1) both` }}>
                      <div style={{ fontSize: 'clamp(24px,4vh,50px)', marginBottom: 4 }}>{medals[i] || `#${i + 1}`}</div>
                      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(18px,3vh,38px)', color: i === 0 ? C.gold : '#fff' }}>{t.name}</div>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(22px,3.8vh,48px)', color: t.color || C.teal, textShadow: `0 0 18px ${t.color || C.teal}99` }}>{t.score}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div style={{ textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 5, fontSize: 'clamp(11px,1.5vh,18px)', color: 'rgba(255,179,0,.45)' }}>{hasTeams ? 'INDIVIDUAL SCORES' : 'FINAL STANDINGS'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(4px,.7vh,9px)', overflow: 'hidden' }}>
              {scores.slice(0, hasTeams ? 5 : 8).map((s, i) => (
                <div key={(s.jid || s.name) + i} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px,1.6vw,22px)', padding: 'clamp(8px,1.4vh,18px) clamp(16px,2.5vw,32px)', background: i === 0 ? `linear-gradient(90deg, rgba(255,215,0,.2), rgba(255,215,0,.06))` : 'rgba(255,255,255,.05)', border: i === 0 ? `1px solid ${C.gold}44` : '1px solid rgba(255,255,255,.07)', borderRadius: 14, animation: `crPodiumRise .45s ${.4 + i * .07}s ease both`, boxShadow: i === 0 ? `0 0 32px rgba(255,215,0,.18)` : 'none', flexShrink: 0 }}>
                  {s.teamId && teamScores?.length > 0 && <div style={{ width: 'clamp(4px,.5vw,6px)', height: 'clamp(28px,4vh,50px)', borderRadius: 3, background: teamColor(s.teamId, teamScores), flexShrink: 0 }} />}
                  <span style={{ fontSize: 'clamp(22px,3.8vh,48px)', flexShrink: 0 }}>{medals[i] || `${i + 1}.`}</span>
                  <span style={{ flex: 1, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(20px,3.5vh,44px)', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(20px,3.5vh,44px)', color: i === 0 ? C.gold : 'rgba(255,255,255,.65)', flexShrink: 0 }}>{s.score} PTS</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Countdown */}
      <div style={{ flexShrink: 0, textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(10px,1.3vh,16px)', color: 'rgba(255,255,255,.25)', letterSpacing: 2 }}>
        Returning to lobby in {countdown}s
      </div>
    </div>
  );
}

// ─── InstructionBanner ────────────────────────────────────────────────────────
// Small pill at bottom-left — non-blocking, fires only on slot start.
function InstructionBanner({ msg, msgId }) {
  const [visible, setVisible] = useState(false);
  const [text,    setText]    = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!msg) { setVisible(false); return; }
    setText(msg.msg || '');
    setVisible(true);
    timerRef.current = setTimeout(() => setVisible(false), 3000);
    return () => clearTimeout(timerRef.current);
  }, [msg, msgId]);

  if (!visible || !text) return null;

  return (
    <div style={{
      position:      'fixed',
      bottom:        'clamp(16px,2.5vh,32px)',
      left:          'clamp(16px,2vw,28px)',
      zIndex:         420,
      maxWidth:      'min(60vw, 520px)',
      background:    'rgba(0,229,204,.1)',
      border:        `1px solid rgba(0,229,204,.3)`,
      borderRadius:  'clamp(6px,.8vw,12px)',
      padding:       'clamp(7px,1vh,11px) clamp(12px,1.6vw,20px)',
      pointerEvents: 'none',
      animation:     'crInstructionSlide .35s cubic-bezier(.34,1.2,.64,1) both',
      backdropFilter:'blur(10px)',
      boxShadow:     `0 4px 20px rgba(0,0,0,.4)`,
      display:       'flex',
      alignItems:    'center',
      gap:           'clamp(6px,.8vw,10px)',
    }}>
      <span style={{ fontSize: 'clamp(11px,1.1vw,15px)', flexShrink: 0 }}>📱</span>
      <span style={{
        fontFamily:   "'Barlow Condensed', sans-serif",
        fontWeight:    700,
        letterSpacing: 1,
        fontSize:     'clamp(11px,1.2vw,16px)',
        color:         C.teal,
        overflow:     'hidden',
        textOverflow: 'ellipsis',
        whiteSpace:   'nowrap',
      }}>{text}</span>
    </div>
  );
}

// ─── CoopHintOverlay ──────────────────────────────────────────────────────────
// Full-screen overlay that prompts players to text the next letter or full word.
// The letter itself is NOT revealed — players must deduce it from the word/pattern.
function CoopHintOverlay({ hint, onClear }) {
  const autoTimer = useRef(null);

  useEffect(() => {
    clearTimeout(autoTimer.current);
    if (!hint) return;
    // Auto-dismiss after 12 seconds so players can see the board again
    autoTimer.current = setTimeout(onClear, 12000);
    return () => clearTimeout(autoTimer.current);
  }, [hint, onClear]);

  if (!hint) return null;

  const isLast    = hint.isLastLetter;
  const isFirst   = hint.isFirstLetter;
  const letterNum = (hint.letterIdx || 0) + 1;
  const ordinal   = isFirst ? 'FIRST' : `${letterNum}${letterNum === 2 ? 'ND' : letterNum === 3 ? 'RD' : 'TH'}`;

  return (
    <div style={{
      position:      'fixed',
      inset:          0,
      zIndex:         480,
      background:    'rgba(4,6,18,.92)',
      backdropFilter: 'blur(10px)',
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      justifyContent:'center',
      gap:           'clamp(10px,1.6vh,22px)',
      animation:     'crOverlayIn .4s ease both',
      padding:       'clamp(12px,2vw,40px)',
      overflowY:     'auto',
    }}>

      {/* Badge */}
      <div style={{
        fontFamily:   "'Barlow Condensed', sans-serif",
        fontWeight:    800,
        fontSize:     'clamp(12px,1.3vw,17px)',
        letterSpacing: 8,
        color:        isLast ? C.red : C.gold,
        textTransform:'uppercase',
        background:   isLast ? `${C.red}18` : `${C.gold}18`,
        border:       `1px solid ${isLast ? C.red : C.gold}44`,
        borderRadius:  30,
        padding:      '6px 22px',
      }}>
        {isLast ? '🚨 LAST LETTER REMAINING' : '💡 CO-OP HINT'}
      </div>

      {/* Main call-to-action */}
      <div style={{
        fontFamily:   "'Bebas Neue', sans-serif",
        fontSize:     'clamp(36px,9vh,108px)',
        letterSpacing: 4,
        color:         isLast ? C.red : C.gold,
        textShadow:   `0 0 60px ${isLast ? C.red : C.gold}99`,
        textAlign:    'center',
        lineHeight:    1.05,
        animation:    'crHintPulse 2s ease infinite',
      }}>
        {isLast ? (
          <>TEXT THE COMPLETE<br />WORD NOW!</>
        ) : (
          `TEXT THE ${ordinal} LETTER!`
        )}
      </div>

      {/* Current letter pattern — large so it's readable across the room */}
      <div style={{
        fontFamily:   "'Share Tech Mono', monospace",
        fontSize:     'clamp(22px,4vw,68px)',
        letterSpacing: 'clamp(6px,1.2vw,18px)',
        color:         C.teal,
        textShadow:   `0 0 30px ${C.teal}77`,
        background:   'rgba(255,255,255,.04)',
        border:       `1px solid ${C.teal}33`,
        borderRadius:  16,
        padding:      'clamp(10px,1.5vh,18px) clamp(16px,2.5vw,36px)',
        textAlign:    'center',
        maxWidth:     '90vw',
        overflow:     'hidden',
        textOverflow: 'ellipsis',
        whiteSpace:   'nowrap',
      }}>
        {(hint.pattern || '').split(' ').join('  ')}
      </div>

      {/* Prize / instruction line */}
      {!isLast && (
        <div style={{
          fontFamily:  "'Barlow Condensed', sans-serif",
          fontWeight:   700,
          fontSize:    'clamp(18px,2.5vw,36px)',
          color:        C.green,
          textShadow:  `0 0 20px ${C.green}66`,
          textAlign:   'center',
          letterSpacing: 2,
        }}>
          First to text the correct letter wins {hint.prize || 25} pts!
        </div>
      )}

      <div style={{
        fontFamily:  "'Barlow Condensed', sans-serif",
        fontWeight:   600,
        fontSize:    'clamp(14px,1.8vw,26px)',
        color:       'rgba(255,255,255,.5)',
        textAlign:   'center',
        letterSpacing: 1,
      }}>
        {isLast
          ? 'You can see all but the last letter — you know this word!'
          : 'Or text the COMPLETE WORD at any time for full points!'}
      </div>

      {hint.msg && (
        <div style={{
          fontFamily:  "'Barlow Condensed', sans-serif",
          fontWeight:   700,
          fontSize:    'clamp(13px,1.5vw,20px)',
          color:       'rgba(255,255,255,.35)',
          textAlign:   'center',
          letterSpacing: 1,
          maxWidth:    '70vw',
          fontStyle:   'italic',
        }}>
          {hint.msg}
        </div>
      )}
    </div>
  );
}

// ─── SpeedChainOverlay ────────────────────────────────────────────────────────
function SpeedChainOverlay({ data, paused }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (paused) return;
    const update = () => {
      if (!data?.timerStartedAt || !data?.durationMs) { setTimeLeft(0); return; }
      const elapsed = Date.now() - data.timerStartedAt;
      setTimeLeft(Math.max(0, Math.round((data.durationMs - elapsed) / 1000)));
    };
    update();
    const iv = setInterval(update, 500);
    return () => clearInterval(iv);
  }, [data?.timerStartedAt, data?.durationMs, paused]);

  if (!data) return null;

  const isOpen          = data.phase === 'open';
  const isCompleted     = data.status === 'completed';
  const isRevealed      = data.status === 'revealed';
  const answersVisible  = data.answersVisible !== false; // default true
  const slots           = data.chain?.slots || [];
  const rrNames      = data.roundRobinOrder || [];

  return (
    <div style={{
      position:      'fixed',
      inset:          0,
      zIndex:         500,
      background:    'rgba(6,9,20,.93)',
      backdropFilter: 'blur(10px)',
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      justifyContent:'center',
      gap:           'clamp(8px,2vh,28px)',
      animation:     'crOverlayIn .4s ease both',
      padding:       'clamp(10px,2vh,26px) clamp(12px,2.5vw,36px)',
      overflowY:     'auto',
    }}>
      {/* Header */}
      <div style={{
        fontFamily:   "'Barlow Condensed', sans-serif",
        fontWeight:    700,
        fontSize:     'clamp(15px,3.5vh,42px)',
        letterSpacing: 8,
        color:         C.gold,
        textTransform: 'uppercase',
        flexShrink:    0,
      }}>
        {isCompleted ? '🏆 SPEED CHAIN SOLVED!' : isRevealed ? '👁 ANSWER REVEALED' : '⚡ SPEED CHAIN'}
      </div>

      {/* How-to-play hint — only while active */}
      {!isCompleted && !isRevealed && (
        <div style={{
          fontFamily:   "'Barlow Condensed', sans-serif",
          fontWeight:    600,
          fontSize:     'clamp(11px,2.2vh,24px)',
          letterSpacing: 4,
          color:        'rgba(255,255,255,.4)',
          textTransform: 'uppercase',
          textAlign:    'center',
          flexShrink:    0,
        }}>
          Text each word separately, or both words together with a space
        </div>
      )}

      {/* Chain visual */}
      <div style={{
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           'clamp(5px,1.2vh,14px)',
        background:    'rgba(255,255,255,.04)',
        border:        `1px solid ${C.gold}33`,
        borderRadius:  18,
        padding:       'clamp(12px,2.5vh,34px) clamp(16px,3vw,52px)',
        flexShrink:    0,
      }}>
        {/* TOP anchor */}
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(20px,5.5vh,64px)', color: C.gold, letterSpacing: 6, lineHeight: 1 }}>
          {data.chain?.top || ''}
        </div>
        <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(12px,2.2vh,26px)', lineHeight: 1 }}>▼</div>

        {/* Hidden slots */}
        {slots.map((slot, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(5px,1.2vh,14px)' }}>
            <div style={{
              fontFamily:   "'Bebas Neue', sans-serif",
              fontSize:     'clamp(28px,8vh,94px)',
              letterSpacing: 10,
              color:         (slot.solved && answersVisible) ? C.green : (isRevealed && answersVisible) ? C.red : C.teal,
              textShadow:   (slot.solved && answersVisible) ? `0 0 30px ${C.green}99` : (isRevealed && answersVisible) ? `0 0 20px ${C.red}66` : `0 0 20px ${C.teal}66`,
              background:    (slot.solved && answersVisible) ? `${C.green}14` : (isRevealed && answersVisible) ? `${C.red}10` : `${C.teal}10`,
              border:        `2px solid ${(slot.solved && answersVisible) ? C.green : (isRevealed && answersVisible) ? C.red : C.teal}44`,
              borderRadius:  14,
              padding:       'clamp(7px,1.3vh,17px) clamp(14px,2.5vw,36px)',
              minWidth:      'clamp(130px,20vw,280px)',
              textAlign:     'center',
              transition:    'all .4s ease',
              lineHeight:    1,
            }}>
              {(slot.solved && answersVisible) ? slot.word : (isRevealed && answersVisible) ? (slot.word || '?') : (slot.pattern || `${slot.firstLetter || '_'} _ _ _ _`)}
            </div>
            {i < slots.length - 1 && <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(12px,2.2vh,26px)', lineHeight: 1 }}>▼</div>}
          </div>
        ))}

        <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(12px,2.2vh,26px)', lineHeight: 1 }}>▼</div>
        {/* BOTTOM anchor */}
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(20px,5.5vh,64px)', color: C.teal, letterSpacing: 6, lineHeight: 1 }}>
          {data.chain?.bottom || ''}
        </div>
      </div>

      {/* Active player / Open state */}
      {!isCompleted && !isRevealed && (
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          {isOpen ? (
            <div style={{
              fontFamily:   "'Bebas Neue', sans-serif",
              fontSize:     'clamp(28px,8vh,94px)',
              color:         C.red,
              letterSpacing: 6,
              textShadow:   `0 0 40px ${C.red}88`,
              lineHeight:    1,
            }}>
              OPEN TO ALL!
            </div>
          ) : (
            <>
              <div style={{ fontSize: 'clamp(12px,2.2vh,24px)', color: 'rgba(255,255,255,.4)', letterSpacing: 3, marginBottom: 4, fontFamily: "'Barlow Condensed', sans-serif" }}>NOW ANSWERING</div>
              <div style={{
                fontFamily:   "'Bebas Neue', sans-serif",
                fontSize:     'clamp(28px,8vh,94px)',
                letterSpacing: 4,
                color:         C.gold,
                textShadow:   `0 0 50px ${C.gold}88`,
                animation:    'crPulseGold 2s ease infinite',
                maxWidth:     '80vw',
                overflow:     'hidden',
                textOverflow: 'ellipsis',
                whiteSpace:   'nowrap',
                lineHeight:    1,
              }}>
                {data.activePlayer?.name || ''}
              </div>
            </>
          )}
        </div>
      )}

      {isCompleted && (
        <div style={{ textAlign: 'center', maxWidth: '85vw', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(28px,8vh,94px)', color: C.gold, letterSpacing: 5, textShadow: `0 0 50px ${C.gold}88`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1 }}>
            {data.winner || ''}
          </div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(18px,3.5vh,42px)', color: C.green, fontWeight: 700, marginTop: 6 }}>
            +{data.prize || 150} PTS
          </div>
        </div>
      )}

      {/* Round-robin order */}
      {!isCompleted && !isRevealed && rrNames.length > 0 && (
        <div style={{ display: 'flex', gap: 'clamp(8px,1vw,14px)', flexWrap: 'wrap', justifyContent: 'center', flexShrink: 0 }}>
          {rrNames.map((name, i) => {
            const isActive = !isOpen && name === (data.activePlayer?.name);
            return (
              <div key={i} style={{
                fontFamily:  "'Barlow Condensed', sans-serif",
                fontWeight:   700,
                fontSize:    'clamp(13px,2.5vh,28px)',
                color:        isActive ? C.gold : 'rgba(255,255,255,.35)',
                background:   isActive ? `${C.gold}22` : 'rgba(255,255,255,.04)',
                border:       `1px solid ${isActive ? C.gold + '66' : 'rgba(255,255,255,.1)'}`,
                borderRadius:  10,
                padding:      'clamp(4px,.8vh,10px) clamp(10px,1.4vw,18px)',
                transition:   'all .3s ease',
              }}>
                {i + 1}. {name}
              </div>
            );
          })}
        </div>
      )}

      {/* Countdown timer */}
      {!isCompleted && !isRevealed && (
        <div style={{
          fontFamily:   "'Bebas Neue', sans-serif",
          fontSize:     'clamp(42px,9.5vh,116px)',
          color:         timeLeft <= 10 ? C.red : C.teal,
          letterSpacing: 4,
          textShadow:   `0 0 30px ${timeLeft <= 10 ? C.red : C.teal}66`,
          transition:   'color .3s ease',
          lineHeight:    1,
          flexShrink:    0,
        }}>
          {timeLeft}s
        </div>
      )}
    </div>
  );
}

// ─── BonusRoundOverlay ────────────────────────────────────────────────────────
function BonusRoundOverlay({ data, paused }) {
  const [timeLeft, setTimeLeft]               = useState(0);
  const [transitionLeft, setTransitionLeft]   = useState(0);

  // Running — use remainingMs (server snapshot) minus time elapsed on the CLIENT since event arrived.
  // Using clientTimerStartedAt (set when the event was received) avoids server/client clock skew.
  useEffect(() => {
    if (!data?.timerRunning || paused) return;
    const update = () => {
      if (!data?.clientTimerStartedAt || data?.remainingMs == null) { setTimeLeft(0); return; }
      const remaining = Math.max(0, data.remainingMs - (Date.now() - data.clientTimerStartedAt));
      setTimeLeft(Math.round(remaining / 1000));
    };
    update();
    const iv = setInterval(update, 500);
    return () => clearInterval(iv);
  }, [data?.clientTimerStartedAt, data?.timerRunning, data?.remainingMs, paused]);

  // Paused — show remainingMs directly
  useEffect(() => {
    if (data?.timerRunning && !paused) return;
    setTimeLeft(Math.round((data?.remainingMs ?? 0) / 1000));
  }, [data?.timerRunning, data?.remainingMs, paused]);

  // Transition countdown between chains
  useEffect(() => {
    if (!data?.isTransitioning || !data?.transitionStartedAt || !data?.transitionMs) {
      setTransitionLeft(0);
      return;
    }
    const update = () => {
      const elapsed   = Date.now() - data.transitionStartedAt;
      const remaining = Math.max(0, data.transitionMs - elapsed);
      setTransitionLeft(Math.ceil(remaining / 1000));
    };
    update();
    const iv = setInterval(update, 200);
    return () => clearInterval(iv);
  }, [data?.isTransitioning, data?.transitionStartedAt, data?.transitionMs]);

  if (!data) return null;

  const chains            = data.chains || [];
  const chainIdx          = data.currentChainIdx || 0;
  // Prefer flat activeChain (mirrors speed chain) — falls back to nested chains[idx] for safety
  const activeChain       = data.activeChain || chains[chainIdx] || {};
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[BonusRoundOverlay] chainIdx=${chainIdx} activeChain slots:`, activeChain.slots?.map(s => ({ word: s.word, solved: s.solved })));
  }
  const completedChainIdx = data.completedChainIdx;
  const completedChain    = typeof completedChainIdx === 'number' ? (chains[completedChainIdx] || {}) : null;
  const timerPaused       = !data.timerRunning;
  const isCompleted       = data.status === 'completed';
  const isTransitioning   = !!data.isTransitioning;
  const answersVisible    = data.answersVisible !== false; // default true

  // Helper to render a chain card (used for both active and completed views)
  const renderChain = (chain, opts = {}) => {
    const { forceReveal = false, accentColor = C.purple } = opts;
    return (
      <div style={{
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           'clamp(5px,1.2vh,14px)',
        background:    'rgba(255,255,255,.04)',
        border:        `1px solid ${accentColor}33`,
        borderRadius:  18,
        padding:       'clamp(12px,2.5vh,34px) clamp(16px,3vw,52px)',
        flexShrink:    0,
      }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(18px,5vh,58px)', color: C.gold, letterSpacing: 6, lineHeight: 1 }}>{chain.top || ''}</div>
        <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(12px,2.2vh,26px)', lineHeight: 1 }}>▼</div>
        {(chain.slots || []).map((slot, i) => {
          const revealed = (forceReveal || slot.solved) && answersVisible;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(5px,1.2vh,14px)' }}>
              <div style={{
                fontFamily:   "'Bebas Neue', sans-serif",
                fontSize:     'clamp(26px,7.5vh,88px)',
                letterSpacing: 9,
                color:         revealed ? C.green : C.teal,
                textShadow:   revealed ? `0 0 24px ${C.green}88` : `0 0 16px ${C.teal}55`,
                background:    revealed ? `${C.green}12` : `${C.teal}08`,
                border:        `1px solid ${revealed ? C.green : C.teal}33`,
                borderRadius:  13,
                padding:       'clamp(6px,1.2vh,16px) clamp(14px,2.5vw,36px)',
                minWidth:      'clamp(120px,18vw,260px)',
                textAlign:     'center',
                lineHeight:    1,
                transition:    'all .4s ease',
              }}>
                {revealed ? (slot.word || '') : (slot.pattern || `${slot.firstLetter || '_'} _ _ _`)}
              </div>
              {i < (chain.slots || []).length - 1 && <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(12px,2.2vh,26px)', lineHeight: 1 }}>▼</div>}
            </div>
          );
        })}
        <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 'clamp(12px,2.2vh,26px)', lineHeight: 1 }}>▼</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(18px,5vh,58px)', color: C.teal, letterSpacing: 6, lineHeight: 1 }}>{chain.bottom || ''}</div>
      </div>
    );
  };

  return (
    <div style={{
      position:      'fixed',
      inset:          0,
      zIndex:         500,
      background:    'rgba(6,9,20,.93)',
      backdropFilter: 'blur(10px)',
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      justifyContent:'center',
      gap:           'clamp(8px,2vh,28px)',
      animation:     'crOverlayIn .4s ease both',
      padding:       'clamp(10px,2vh,26px) clamp(12px,2.5vw,36px)',
      overflowY:     'auto',
    }}>
      {/* Header */}
      <div style={{
        fontFamily:   "'Bebas Neue', sans-serif",
        fontSize:     'clamp(20px,5.5vh,64px)',
        letterSpacing: 8,
        color:         isTransitioning ? C.green : C.purple,
        textShadow:   `0 0 50px ${isTransitioning ? C.green : C.purple}88`,
        animation:    'crBonusPulse 2.5s ease infinite',
        flexShrink:    0,
      }}>
        {isCompleted ? '💎 BONUS ROUND COMPLETE!' : isTransitioning ? `✅ CHAIN ${(completedChainIdx ?? 0) + 1} SOLVED!` : '💎 BONUS ROUND'}
      </div>

      {/* Chain progress indicators — always visible */}
      <div style={{ display: 'flex', gap: 'clamp(8px,1.2vw,16px)', flexShrink: 0 }}>
        {chains.map((ch, i) => (
          <div key={i} style={{
            fontFamily:  "'Barlow Condensed', sans-serif",
            fontWeight:   700,
            fontSize:    'clamp(13px,2.5vh,28px)',
            color:        ch.solved ? C.green : i === chainIdx ? C.gold : 'rgba(255,255,255,.3)',
            background:   ch.solved ? `${C.green}18` : i === chainIdx ? `${C.gold}18` : 'rgba(255,255,255,.04)',
            border:       `2px solid ${ch.solved ? C.green : i === chainIdx ? C.gold : 'rgba(255,255,255,.1)'}55`,
            borderRadius:  12,
            padding:      'clamp(6px,1.2vh,15px) clamp(12px,2vw,26px)',
            transition:   'all .3s ease',
          }}>
            {ch.solved ? '✓' : `CHAIN ${i + 1}`}
          </div>
        ))}
      </div>

      {/* During transition: show completed chain with all answers revealed */}
      {isTransitioning && completedChain && renderChain(completedChain, { forceReveal: true, accentColor: C.green })}

      {/* During transition: small countdown */}
      {isTransitioning && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(4px,.8vh,10px)', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(14px,2.5vh,30px)', letterSpacing: 4, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase' }}>
            Next chain in
          </div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(36px,7vh,88px)', lineHeight: 1, color: C.gold, textShadow: `0 0 30px ${C.gold}88`, animation: 'crPulseGold 1s ease infinite' }}>
            {transitionLeft}s
          </div>
        </div>
      )}

      {/* Active chain visual — shown only during normal gameplay */}
      {!isTransitioning && !isCompleted && renderChain(activeChain)}

      {/* Active player */}
      {!isCompleted && !isTransitioning && (
        <div style={{ textAlign: 'center', maxWidth: '85vw', flexShrink: 0 }}>
          <div style={{ fontSize: 'clamp(12px,2.2vh,24px)', color: 'rgba(255,255,255,.4)', letterSpacing: 3, marginBottom: 4, fontFamily: "'Barlow Condensed', sans-serif" }}>NOW ANSWERING</div>
          <div style={{
            fontFamily:   "'Bebas Neue', sans-serif",
            fontSize:     'clamp(26px,7.5vh,88px)',
            letterSpacing: 4,
            color:         C.gold,
            textShadow:   `0 0 40px ${C.gold}77`,
            animation:    'crPulseGold 2s ease infinite',
            overflow:     'hidden',
            textOverflow: 'ellipsis',
            whiteSpace:   'nowrap',
            lineHeight:    1,
          }}>
            {data.activePlayerName || ''}
          </div>
        </div>
      )}

      {isCompleted && (
        <div style={{ textAlign: 'center', maxWidth: '85vw', flexShrink: 0 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(26px,7.5vh,88px)', color: C.gold, letterSpacing: 4, textShadow: `0 0 40px ${C.gold}77`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1 }}>{data.winner || ''}</div>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(18px,3.5vh,42px)', color: C.green, fontWeight: 700, marginTop: 6 }}>+{data.prize || 300} PTS</div>
        </div>
      )}

      {/* Timer */}
      {!isCompleted && !isTransitioning && (
        <div style={{
          fontFamily:   "'Bebas Neue', sans-serif",
          fontSize:     'clamp(42px,9.5vh,116px)',
          color:         timerPaused ? 'rgba(255,255,255,.3)' : timeLeft <= 10 ? C.red : C.teal,
          letterSpacing: 4,
          textShadow:   timerPaused ? 'none' : `0 0 30px ${timeLeft <= 10 ? C.red : C.teal}66`,
          transition:   'color .3s ease',
          lineHeight:    1,
          flexShrink:    0,
        }}>
          {timeLeft}s {timerPaused ? '(PAUSED)' : ''}
        </div>
      )}

      {timerPaused && !isCompleted && !isTransitioning && (
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(14px,2.5vh,26px)', color: 'rgba(255,255,255,.3)', letterSpacing: 2, flexShrink: 0 }}>
          ADMIN: CLICK RESUME TO CONTINUE
        </div>
      )}
    </div>
  );
}

// ─── Main board ───────────────────────────────────────────────────────────────
export default function ChainReactionBoard({ gameId, onExit, tvMode = false, onGameCompleted, paused = false }) {
  const [game,            setGame]            = useState(null);
  const [slots,           setSlots]           = useState({});
  const [currentSlot,     setCurrentSlot]     = useState(0);
  const [scores,          setScores]          = useState([]);
  const [teamScores,      setTeamScores]      = useState([]);
  const [status,          setStatus]          = useState('loading');
  const [lastSolved,      setLastSolved]      = useState(null);
  const [wrongFlash,      setWrongFlash]      = useState(null);
  const [currentRound,    setCurrentRound]    = useState(0);
  const [totalRounds,     setTotalRounds]     = useState(1);
  const [roundTransition, setRoundTransition] = useState(null);
  const [autoCountdown,   setAutoCountdown]   = useState(null);
  const [hostMsg,         setHostMsg]         = useState(null);
  const [hostMsgId,       setHostMsgId]       = useState(0);
  const [instructionMsg,  setInstructionMsg]  = useState(null);
  const [instructionMsgId, setInstructionMsgId] = useState(0);
  const [coopHint,        setCoopHint]        = useState(null);
  const [speedChain,      setSpeedChain]      = useState(null);
  const [bonusRound,      setBonusRound]      = useState(null);

  const lastSolvedTimer  = useRef(null);
  const wrongFlashTimer  = useRef(null);
  const completedTimer   = useRef(null);
  const hostMsgTimer     = useRef(null);
  const instructionTimer = useRef(null);

  // ── Initial REST load ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!gameId) return;
    fetch(`${API}/chainreaction/${gameId}`)
      .then(r => r.ok ? r.json() : null)
      .then(g => {
        if (!g) { setStatus('waiting'); return; }
        setGame(g);
        setStatus(g.status || 'waiting');
        if (typeof g.currentRound === 'number') setCurrentRound(g.currentRound);
        if (typeof g.totalRounds  === 'number') setTotalRounds(g.totalRounds);

        const raw = g.slots || {};
        const built = {};
        Object.keys(raw).forEach(k => {
          const s = raw[k];
          const lr = s.lettersRevealed || 0;
          built[Number(k)] = {
            word: s.word || '', lettersRevealed: lr, solved: s.solved || false,
            solvedBy: s.solvedBy || '', solvedByName: s.solvedByName || '',
            letterPattern: s.letterPattern || buildLetterPattern(s.word, lr),
          };
        });
        setSlots(built);
        if (typeof g.currentSlot === 'number') setCurrentSlot(g.currentSlot);
        if (g.scores)     setScores(normScores(g.scores));
        if (g.teamScores) setTeamScores(normScores(g.teamScores));
        if (g.status === 'completed' && tvMode && onGameCompleted) {
          completedTimer.current = setTimeout(onGameCompleted, 60000);
        }
      })
      .catch(() => { setStatus('waiting'); });
  }, [gameId]);

  // ── Ably subscriptions ───────────────────────────────────────────────────────
  useAblyChannel(gameId ? `chainreaction:game:${gameId}` : null, {
    'game-state': (data) => {
      setStatus(data.state || 'active');
      if (data.scores)     setScores(normScores(data.scores));
      if (data.teamScores) setTeamScores(normScores(data.teamScores));
      if (data.state === 'completed' && tvMode && onGameCompleted) {
        completedTimer.current = setTimeout(onGameCompleted, 60000);
      }
    },

    'host-message': (data) => {
      setHostMsg(data);
      setHostMsgId(n => n + 1);
      clearTimeout(hostMsgTimer.current);
      hostMsgTimer.current = setTimeout(() => setHostMsg(null), 6000);
    },

    'letter-revealed': (data) => {
      const { slotIdx, lettersRevealed, letterPattern, revealedPositions } = data;
      setSlots(prev => {
        const slot = prev[slotIdx];
        if (!slot) return prev;
        return { ...prev, [slotIdx]: { ...slot, lettersRevealed, revealedPositions: revealedPositions || slot.revealedPositions, letterPattern: letterPattern || buildLetterPattern(slot.word, revealedPositions || lettersRevealed) } };
      });
    },

    'slot-solved': (data) => {
      const { slotIdx, word, playerName, teamId, points, scores: ns, teamScores: nts, letterPattern } = data;
      setSlots(prev => ({
        ...prev,
        [slotIdx]: { ...(prev[slotIdx] || {}), word, lettersRevealed: word ? word.length : 0, solved: true, solvedBy: data.jid || '', solvedByName: playerName || '', letterPattern: letterPattern || (word ? word.toUpperCase().split('').join(' ') : '') },
      }));
      if (ns)  setScores(normScores(ns));
      if (nts) setTeamScores(normScores(nts));
      clearTimeout(lastSolvedTimer.current);
      setLastSolved({ word, playerName, points });
      lastSolvedTimer.current = setTimeout(() => setLastSolved(null), 2500);
      setCoopHint(null); // clear coop hint overlay on slot solve
    },

    'slot-advanced': (data) => {
      if (typeof data.slotIdx === 'number') setCurrentSlot(data.slotIdx);
    },

    'wrong-guess': (data) => {
      clearTimeout(wrongFlashTimer.current);
      setWrongFlash({ playerName: data.playerName, guess: data.guess });
      wrongFlashTimer.current = setTimeout(() => setWrongFlash(null), 1500);
    },

    'coop-attempt': (data) => {
      clearTimeout(wrongFlashTimer.current);
      setWrongFlash({ playerName: data.playerName, guess: data.guess });
      wrongFlashTimer.current = setTimeout(() => setWrongFlash(null), 1200);
    },

    'round-complete': (data) => {
      if (data.scores)     setScores(normScores(data.scores));
      if (data.teamScores) setTeamScores(normScores(data.teamScores));
      if (data.hasMoreRounds) {
        setRoundTransition({ fromRound: data.roundIdx, toRound: data.roundIdx + 1, total: data.totalRounds, hasMore: true });
      }
    },

    'round-start': (data) => {
      setCurrentRound(data.roundIdx);
      setTotalRounds(data.totalRounds);
      setRoundTransition(null);
      setAutoCountdown(null);
      setSpeedChain(null);
      const built = {};
      Object.entries(data.slots || {}).forEach(([k, s]) => {
        built[Number(k)] = { word: s.word, lettersRevealed: 0, solved: false, solvedBy: '', solvedByName: '', letterPattern: '' };
      });
      setSlots(built);
      setCurrentSlot(0);
      if (data.topWord || data.bottomWord) {
        setGame(g => g ? { ...g, chain: { ...g.chain, top: data.topWord, bottom: data.bottomWord } } : g);
      }
      if (data.hostMsg) {
        setHostMsg({ msg: data.hostMsg });
        setHostMsgId(n => n + 1);
        clearTimeout(hostMsgTimer.current);
        hostMsgTimer.current = setTimeout(() => setHostMsg(null), 6000);
      }
    },

    'next-round-countdown': (data) => {
      setAutoCountdown({ nextRoundIdx: data.nextRoundIdx, delayMs: data.delayMs, startedAt: Date.now() });
    },

    'slot-instruction': (data) => {
      setInstructionMsg(data);
      setInstructionMsgId(n => n + 1);
      clearTimeout(instructionTimer.current);
      instructionTimer.current = setTimeout(() => setInstructionMsg(null), 4000);
    },

    'coop-hint': (data) => {
      setCoopHint(data);
    },

    'coop-letter-correct': (data) => {
      if (data.scores) setScores(normScores(data.scores));
      setCoopHint(null); // dismiss hint overlay — someone got the letter
      if (typeof data.slotIdx === 'number' && data.pattern) {
        setSlots(prev => {
          const slot = prev[data.slotIdx];
          if (!slot) return prev;
          const revealed = (data.pattern.split(' ').filter(c => c !== '_')).length;
          return { ...prev, [data.slotIdx]: { ...slot, lettersRevealed: revealed, letterPattern: data.pattern } };
        });
      }
    },

    'speed-chain-start': (data) => {
      setSpeedChain({ ...data, status: 'active', phase: 'roundrobin' });
    },

    'speed-chain-prompt': (data) => {
      setSpeedChain(prev => {
        if (!prev) return null;
        const next = { ...prev, ...data };
        if (data.chain) next.chain = data.chain;
        if (typeof data.currentSlot === 'number') next.currentSlot = data.currentSlot;
        return next;
      });
    },

    'speed-chain-slot-solved': (data) => {
      setSpeedChain(prev => {
        if (!prev) return null;
        const newSlots = [...(prev.chain?.slots || [])];
        if (newSlots[data.slotIdx]) newSlots[data.slotIdx] = { ...newSlots[data.slotIdx], solved: true, word: data.word };
        return { ...prev, chain: { ...prev.chain, slots: newSlots }, currentSlot: data.nextSlot ?? prev.currentSlot };
      });
    },

    'speed-chain-wrong': () => {},

    'speed-chain-open': (data) => {
      setSpeedChain(prev => {
        if (!prev) return null;
        const next = { ...prev, phase: 'open', ...data };
        if (data.chain) next.chain = data.chain;
        if (typeof data.currentSlot === 'number') next.currentSlot = data.currentSlot;
        return next;
      });
    },

    'speed-chain-no-answer': () => {
      setSpeedChain(prev => prev ? { ...prev, status: 'no-answer' } : null);
      setTimeout(() => setSpeedChain(null), 3500);
    },

    'speed-chain-complete': (data) => {
      setSpeedChain(prev => {
        if (!prev) return null;
        const next = { ...prev, status: 'completed', winner: data.winnerName, prize: data.prize };
        if (data.chain) next.chain = data.chain;
        return next;
      });
      if (data.scores) setScores(normScores(data.scores));
      setTimeout(() => setSpeedChain(null), 8000);
    },

    'speed-chain-reveal': (data) => {
      setSpeedChain(prev => prev ? { ...prev, status: 'revealed', chain: data.chain } : null);
      setTimeout(() => setSpeedChain(null), 8000);
    },

    'speed-chain-set-reveal': (data) => {
      setSpeedChain(prev => prev ? { ...prev, answersVisible: data.show } : null);
    },

    'bonus-set-reveal': (data) => {
      setBonusRound(prev => prev ? { ...prev, answersVisible: data.show } : null);
    },

    'bonus-round-start': (data) => {
      console.log('[BonusRound] bonus-round-start:', data);
      setBonusRound({ ...data, status: 'active', clientTimerStartedAt: Date.now() });
    },

    'bonus-chain-start': (data) => {
      console.log('[BonusRound] bonus-chain-start:', data);
      setBonusRound(prev => {
        if (!prev) return null;
        const activePlayerName = data.activePlayerName || data.activePlayer?.name || prev.activePlayerName;
        const activeChain = data.activeChain || data.chain || prev.activeChain;
        return { ...prev, ...data, activeChain, activePlayerName, timerRunning: true, isTransitioning: false, clientTimerStartedAt: Date.now() };
      });
    },

    'bonus-chain-transition': (data) => {
      setBonusRound(prev => {
        if (!prev) return null;
        const chains = [...(prev.chains || [])];
        let completedChainData = prev.activeChain || chains[data.completedChainIdx] || {};
        if (typeof data.completedChainIdx === 'number' && chains[data.completedChainIdx]) {
          // Mark chain solved and reveal all slot words so they show during the transition
          const ch = { ...chains[data.completedChainIdx], solved: true };
          ch.slots = (ch.slots || []).map(s => ({ ...s, solved: true }));
          chains[data.completedChainIdx] = ch;
          completedChainData = ch;
        }
        return {
          ...prev,
          chains,
          activeChain: completedChainData,
          timerRunning: false,
          isTransitioning: true,
          completedChainIdx: data.completedChainIdx,
          transitionMs: data.transitionMs,
          transitionStartedAt: data.transitionStartedAt,
          currentChainIdx: data.nextChainIdx,
          nextChain: data.nextChain,
          remainingMs: data.remainingMs,
        };
      });
    },

    'bonus-slot-solved': (data) => {
      console.log('[BonusRound] bonus-slot-solved received:', data);
      setBonusRound(prev => {
        if (!prev) return null;
        // Update flat activeChain (mirrors speed chain) — this drives the display
        const activeChain = { ...(prev.activeChain || {}) };
        const activeSlots = [...(activeChain.slots || [])];
        if (activeSlots[data.slotIdx] !== undefined) {
          activeSlots[data.slotIdx] = { ...activeSlots[data.slotIdx], solved: true, word: data.word };
        }
        activeChain.slots = activeSlots;
        // Also update nested chains array for progress dots
        const chains = [...(prev.chains || [])];
        const chain  = { ...chains[data.chainIdx] };
        const slots  = [...(chain.slots || [])];
        if (slots[data.slotIdx] !== undefined) slots[data.slotIdx] = { ...slots[data.slotIdx], solved: true, word: data.word };
        chain.slots = slots;
        chains[data.chainIdx] = chain;
        // Pause the timer immediately when the last slot of a chain is solved,
        // without waiting for bonus-chain-transition (which arrives after async Firebase ops)
        const timerRunning = data.chainComplete ? false : prev.timerRunning;
        return { ...prev, activeChain, chains, timerRunning };
      });
    },

    'bonus-chain-reveal': (data) => {
      setBonusRound(prev => {
        if (!prev) return null;
        const chains = [...(prev.chains || [])];
        if (chains[data.chainIdx]) chains[data.chainIdx] = { ...chains[data.chainIdx], ...data.chain };
        const activeChain = data.activeChain || data.chain || prev.activeChain;
        return { ...prev, chains, activeChain };
      });
    },

    'bonus-player-change': (data) => {
      setBonusRound(prev => prev ? { ...prev, activePlayer: data.activePlayer, activePlayerName: data.activePlayer?.name, roundRobinIdx: data.roundRobinIdx } : null);
    },

    'bonus-round-complete': (data) => {
      setBonusRound(prev => prev ? { ...prev, status: 'completed', winner: data.winnerName, prize: data.prize } : null);
      if (data.scores) setScores(normScores(data.scores));
      setTimeout(() => setBonusRound(null), 10000);
    },

    'bonus-round-end': (data) => {
      setBonusRound(prev => prev ? { ...prev, status: 'revealed' } : null);
      setTimeout(() => setBonusRound(null), 8000);
    },
  });

  useEffect(() => () => {
    clearTimeout(lastSolvedTimer.current);
    clearTimeout(wrongFlashTimer.current);
    clearTimeout(completedTimer.current);
    clearTimeout(hostMsgTimer.current);
    clearTimeout(instructionTimer.current);
  }, []);

  // ── Derived values ───────────────────────────────────────────────────────────
  // slots = { 0: MOUNTAIN, 1: COFFEE, … } — hidden words only.
  // START (game.chain.top) and END (game.chain.bottom) are rendered separately.
  const slotKeys       = Object.keys(slots).map(Number).sort((a, b) => a - b);
  const totalSlots     = slotKeys.length;
  const chainCompleted = status === 'completed' || (slotKeys.length > 0 && slotKeys.every(k => slots[k]?.solved));

  if (status === 'loading' || (!game && status === 'loading')) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(18px,2.2vw,28px)', letterSpacing: 6, color: 'rgba(255,255,255,.3)' }}>
        LOADING CHAIN REACTION…
      </div>
    );
  }

  const mode = game?.mode || 'buzzin';

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, fontFamily: "'Barlow', sans-serif", color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{STYLES}</style>
      <Orbs />

      <HostMessageBar msg={hostMsg} msgId={hostMsgId} />
      <FlashBanner lastSolved={lastSolved} wrongFlash={wrongFlash} />
      <RoundTransitionScreen data={roundTransition} scores={scores} teamScores={teamScores} />
      <CountdownBar countdown={autoCountdown} />
      <InstructionBanner msg={instructionMsg} msgId={instructionMsgId} />
      {coopHint && status === 'active' && <CoopHintOverlay hint={coopHint} onClear={() => setCoopHint(null)} />}
      {speedChain && <SpeedChainOverlay data={speedChain} paused={paused} />}
      {bonusRound && <BonusRoundOverlay data={bonusRound} paused={paused} />}

      {status === 'waiting' && <WaitingScreen game={game} currentRound={currentRound} totalRounds={totalRounds} />}

      {status === 'completed' && <CompletedScreen game={game} slots={slots} scores={scores} teamScores={teamScores} onDismiss={() => setStatus('waiting')} />}

      {status === 'active' && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden',
          position: 'relative', zIndex: 1,
        }}>
          {/* LEFT: Chain */}
          <div style={{ flex: '0 0 65%', display: 'flex', flexDirection: 'column', padding: 'clamp(16px,2.5vh,36px) clamp(16px,2vw,32px) clamp(16px,2.5vh,36px) clamp(20px,3vw,48px)', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,.06)' }}>
            <div style={{ marginBottom: 'clamp(10px,1.5vh,20px)', animation: 'crFadeUp .4s ease both', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: 6, fontSize: 'clamp(11px,1.1vw,15px)', color: 'rgba(255,255,255,.3)', textTransform: 'uppercase' }}>
                {chainCompleted ? '🔗 Chain Complete' : `🔗 Link ${currentSlot + 1} of ${totalSlots}`}
              </div>
              <RoundBadge current={currentRound} total={totalRounds} />
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0, justifyContent: totalSlots + 2 <= 7 ? 'center' : 'flex-start' }}>
              {/* START — always visible, sourced from game.chain.top */}
              <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                <SlotCard slotData={{ word: game?.chain?.top || '' }} isTop={true} totalSlots={totalSlots + 2} />
                <Connector solved={slotKeys.length > 0 && !!slots[slotKeys[0]]?.solved} />
              </div>

              {/* Hidden guessable slots — slot 0 is active immediately on game start */}
              {slotKeys.map((k, i) => {
                const isActive = k === currentSlot;
                const isSolved = slots[k]?.solved || false;
                return (
                  <div key={k} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                    <SlotCard slotData={slots[k]} idx={i + 1} isActive={isActive} isSolved={isSolved} isTop={false} isBottom={false} mode={mode} totalSlots={totalSlots + 2} />
                    <Connector solved={isSolved} />
                  </div>
                );
              })}

              {/* END — always visible, sourced from game.chain.bottom */}
              <SlotCard slotData={{ word: game?.chain?.bottom || '' }} isBottom={true} totalSlots={totalSlots + 2} />
            </div>
          </div>

          {/* RIGHT: Leaderboard */}
          <div style={{ flex: '0 0 35%', display: 'flex', flexDirection: 'column', padding: 'clamp(16px,2.5vh,36px) clamp(20px,2.8vw,44px) clamp(16px,2.5vh,36px) clamp(16px,2vw,28px)', overflowY: 'auto', background: 'rgba(0,0,0,.22)' }}>
            <LeaderboardPanel
              scores={scores} teamScores={teamScores} game={game} status={status}
              currentSlot={currentSlot} totalMiddleSlots={totalSlots}
              currentRound={currentRound} totalRounds={totalRounds}
            />
          </div>
        </div>
      )}

      {onExit && !tvMode && (
        <button onClick={onExit} style={{ position: 'fixed', top: 14, right: 18, zIndex: 600, background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 10, color: 'rgba(255,255,255,.38)', cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(12px,1.3vw,16px)', letterSpacing: 3, padding: 'clamp(10px,1.2vh,14px) clamp(16px,1.8vw,22px)', minHeight: 44 }}>EXIT</button>
      )}
    </div>
  );
}
